import CustomSelect from '../../../DesignSystem/Dropdowns/CustomSelect';
import NewDateField from '../../../DesignSystem/Inputs/NewDateField';
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import Sidebar from "../../../DesignSystem/Sidebar";
import Breadcrumb from "../../../DesignSystem/Breadcrumb/Breadcrumb";
import NewInputField from "../../../DesignSystem/Inputs/NewInputField";
import IconButton from "../../../DesignSystem/Buttons/IconButton";
import { ReactComponent as AddIcon } from "../../../../assets/icons/Plus-icon.svg";
import { ReactComponent as OpenIcon } from "../../../../assets/icons/aml/arrow-right.svg";
import { useClientRequest, clientPath } from "./clientApi";
import { pensionCalculators } from "../access";
import { clientAge } from "./profileDefaults";
import "../../../../css/FinancialPromotions/FinancialPromotionsSubmission.css";
import "../../../../css/Marketing/WebsiteCharges.css";
import "../../../../css/DesignSystem/Inputs/InputField.css";
import Tabs from "../../../DesignSystem/Tabs/Tabs";
import SearchField from "../../../DesignSystem/Inputs/SearchField";
import Popup from "../../../DesignSystem/Popups/Popup";
import { ReactComponent as SortIcon } from "../../../../assets/icons/paraplanningDashboard/Sort.svg";
import Pagination from "../../../DesignSystem/Pagination/Pagination";

export default function ClientDashboard() {
  const navigate = useNavigate();
  const clientRequest = useClientRequest();
  const formRef = useRef(null);
  const [sort, setSort] = useState({ key: "updatedAt", direction: -1 });
  const [access, setAccess] = useState(null);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [page, setPage] = useState(1);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [creating, setCreating] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [profile, setProfile] = useState({
    name: "",
    dateOfBirth: "",
    retirementAge: "",
  });
  const [adviserEmail, setAdviserEmail] = useState("");
  useEffect(() => {
    if (!clientRequest) return;
    let active = true;
    setLoading(true);
    setError("");
    Promise.all([
      clientRequest("pension-client-access"),
      clientRequest("pension-clients"),
    ])
      .then(([principal, data]) => {
        if (!active) return;
        setAccess(principal);
        setClients(data.clients);
        setAdviserEmail(
          principal.role === "Advisers"
            ? principal.email
            : principal.adviserEmails[0] || "",
        );
      })
      .catch((failure) => {
        if (active) setError(failure.message);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [clientRequest]);
  async function create(event) {
    event.preventDefault();
    if (creating) return;
    setCreating(true);
    setError("");
    try {
      const client = await clientRequest("pension-clients", {
        method: "POST",
        action: "create-client",
        body: { profile, adviserEmail },
      });
      navigate(clientPath(client.id, client.ownerEmail));
    } catch (failure) {
      setError(failure.message);
    } finally {
      setCreating(false);
    }
  }
  const change = (event) =>
    setProfile((previous) => ({
      ...previous,
      [event.target.name]: event.target.value,
    }));
  const progress = (client) =>
    Object.values(client.statuses || {}).filter((status) =>
      ["calculated", "review-required"].includes(status),
    ).length;
  const statusCounts = (client) => {
    const counts = { 'Not started': 0, 'In progress': 0, Complete: 0 };
    pensionCalculators.forEach(({ id }) => {
      const status = client.statuses?.[id] || 'not-started';
      const label = status === 'not-started' ? 'Not started' : ['calculated', 'review-required'].includes(status) ? 'Complete' : 'In progress';
      counts[label]++;
    });
    return Object.entries(counts);
  };
  const filtered = clients
    .filter(
      (client) =>
        filter === "all" ||
        (filter === "started"
          ? Object.values(client.statuses || {}).some(
              (status) => status !== "not-started",
            ) && progress(client) < pensionCalculators.length
          : progress(client) === pensionCalculators.length),
    )
    .filter((client) =>
      `${client.profile.name} ${client.ownerEmail} ${client.adviserEmail}`
        .toLowerCase()
        .includes(search.toLowerCase()),
    )
    .sort(
      (a, b) =>
        String(
          sort.key === "name" ? a.profile.name : sort.key === "user" ? a.adviserName || a.adviserEmail || a.ownerEmail || "" : a[sort.key] || "",
        ).localeCompare(
          String(sort.key === "name" ? b.profile.name : sort.key === "user" ? b.adviserName || b.adviserEmail || b.ownerEmail || "" : b[sort.key] || ""),
          "en-GB",
          { numeric: true },
        ) * sort.direction,
    );
  const totalPages = Math.max(1, Math.ceil(filtered.length / 25));
  const pageRows = filtered.slice((page - 1) * 25, page * 25);
  const toggleSort = (key) =>
    setSort((previous) => ({
      key,
      direction: previous.key === key ? -previous.direction : 1,
    }));
  const tabs = [
    { id: "all", label: "All clients", count: clients.length },
    { id: "started", label: "In progress" },
    { id: "complete", label: "All calculated" },
  ];
  return (
    <div className="Main-Page">
      <Helmet>
        <title>Pension Calculator Dashboard</title>
      </Helmet>
      <Sidebar isSetExpanded />
      <main
        className="Main-Container table-contained-page website-page website-dashboard-page column gap-l p-m bg-lavender-web"
        style={{
          minWidth: 0,
          width: "100%",
          alignItems: "stretch",
          boxSizing: "border-box",
        }}
      >
        <Breadcrumb
          items={[
            { label: "Tools & Calculators", to: "/portal/tools-calculators" },
            { label: "Pension Calculators" },
          ]}
        />
        <header
          className="row-wrap gap-m"
          style={{
            width: "100%",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <h1 className="FP_H3">Pension calculator dashboard</h1>
          <div
            className="display-flex gap-s"
            style={{
              alignItems: "center",
              flexWrap: "nowrap",
              minWidth: 0,
              maxWidth: "100%",
            }}
          >
            <SearchField
              id="client-search"
              value={search}
              onChange={(event) => {
                setSearch(event.target.value);
                setPage(1);
              }}
              placeholder="Search clients"
            />
            <IconButton
              icon={<AddIcon aria-hidden />}
              label="Create client"
              disabled={!access}
              onClick={() => setShowForm(true)}
            />
          </div>
        </header>
        {error && (
          <p role="alert" className="FP_Content text-error">
            {error}
          </p>
        )}
        <section
          className="website-dashboard-card table-scroll-card"
          style={{ width: "100%", minWidth: 0 }}
        >
          <div className="website-dashboard-tabs" style={{ marginBottom: "var(--gap-s)" }}>
            <Tabs
              tabs={tabs}
              activeTab={filter}
              onTabChange={(value) => {
                setFilter(value);
                setPage(1);
              }}
            />
          </div>
          {loading ? (
            <p role="status" className="FP_Body p-m">
              Loading clients…
            </p>
          ) : (
            <>
              <div className="website-table-wrap table-rows-scroll-container table-scroll-container">
                <table className="website-table pension-client-table FP_Content">
                  <thead>
                    <tr>
                      <th className="FP_Help_Text_Semibold table-index-column" aria-label="Row number" />
                      <th
                        className="FP_Help_Text_Semibold"
                        aria-sort={
                          sort.key === "name"
                            ? sort.direction === 1
                              ? "ascending"
                              : "descending"
                            : "none"
                        }
                      >
                        <button
                          type="button"
                          className="table-heading-content table-sort-button"
                          onClick={() => toggleSort("name")}
                        >
                          Client
                          <SortIcon aria-hidden />
                        </button>
                      </th>
                      <th className="FP_Help_Text_Semibold">Status</th>
                      {access?.role === "Staff" && <th className="FP_Help_Text_Semibold" aria-sort={sort.key === "user" ? sort.direction === 1 ? "ascending" : "descending" : "none"}>
                        <button type="button" className="table-heading-content table-sort-button" onClick={() => toggleSort("user")}>
                          User
                          <SortIcon aria-hidden />
                        </button>
                      </th>}
                      <th
                        className="FP_Help_Text_Semibold"
                        aria-sort={
                          sort.key === "updatedAt"
                            ? sort.direction === 1
                              ? "ascending"
                              : "descending"
                            : "none"
                        }
                      >
                        <button
                          type="button"
                          className="table-heading-content table-sort-button"
                          onClick={() => toggleSort("updatedAt")}
                        >
                          Last saved
                          <SortIcon aria-hidden />
                        </button>
                      </th>
                      <th className="FP_Help_Text_Semibold">View</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pageRows.map((client, index) => (
                      <tr key={`${client.ownerEmail}/${client.id}`}>
                        <td className="FP_Help_Text table-index-column">{(page - 1) * 25 + index + 1}.</td>
                        <td className="FP_Help_Text">
                          <div className="fit-column gap-xs">
                            <span className="FP_Content_Semibold">{client.profile.name}</span>
                            <span>
                              DOB:{" "}
                              {client.profile.dateOfBirth ? new Intl.DateTimeFormat('en-GB', { day: '2-digit', month: 'short', year: 'numeric', timeZone: 'UTC' }).format(new Date(client.profile.dateOfBirth)) : "Not provided"}
                            </span>
                            <span>Current age: {clientAge(client.profile.dateOfBirth) === '' ? 'Not provided' : clientAge(client.profile.dateOfBirth)}</span>
                            <span>
                              Retirement age:{" "}
                              {client.profile.retirementAge ?? "Not provided"}
                            </span>
                          </div>
                        </td>
                        <td className="FP_Help_Text">
                          <dl className="gap-xs" style={{ display: 'grid', gridTemplateColumns: 'max-content max-content', margin: 0 }}>
                            {statusCounts(client).map(([label, count]) => <React.Fragment key={label}>
                              <dt>{label}</dt>
                              <dd className={label === 'Complete' ? 'FP_Help_Text_Semibold' : undefined} style={{ margin: 0, borderLeft: '1px solid var(--Grey25)', paddingLeft: 'var(--gap-s)' }}>{count}</dd>
                            </React.Fragment>)}
                          </dl>
                        </td>
                        {access?.role === "Staff" && <td className="FP_Help_Text">
                          <div className="fit-column gap-xs">
                            <span className="FP_Help_Text_Semibold">{client.adviserName || ''}</span>
                            <span>{client.adviserEmail || client.ownerEmail}</span>
                          </div>
                        </td>}
                        <td className="FP_Help_Text">
                          <div className="fit-column gap-xs">
                          <span>{client.updatedAt
                            ? new Date(client.updatedAt).toLocaleString("en-GB")
                            : "-"}</span>
                          {client.updatedBy && <span>By: {client.updatedBy}</span>}
                          </div>
                        </td>
                        <td>
                          <IconButton
                            className="pension-client-open"
                            icon={<OpenIcon aria-hidden />}
                            label={`View ${client.profile.name}`}
                            onClick={() =>
                              navigate(clientPath(client.id, client.ownerEmail))
                            }
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {!filtered.length && (
                  <p className="FP_Help_Text_Semibold p-m">
                    {search
                      ? "No clients match your search."
                      : "No clients in this view. Create a client to start."}
                  </p>
                )}
              </div>
              <Pagination
                currentPage={page}
                totalPages={totalPages}
                onPageChange={setPage}
              />
            </>
          )}
        </section>
        <Popup
          className="ds-confirmation-popup--form"
          open={showForm}
          title="New client profile"
          titleClassName="FP_Subtitle_Semibold text-primary-1"
          titleAlign="left"
          showCancel={false}
          confirmLabel="Create client profile"
          busy={creating}
          onCancel={() => setShowForm(false)}
          onConfirm={() => formRef.current?.requestSubmit()}
        >
          <form ref={formRef} onSubmit={create} className="fit-column gap-m">
            <NewInputField
              id="client-name"
              name="name"
              label="Client name"
              value={profile.name}
              onChange={change}
              required
              inputProps={{ maxLength: 200 }}
            />
            <div className="grid2 gap-m">
              <NewDateField
                id="client-dob"
                name="dateOfBirth"
                label="Date of birth" required
                type="date"
                value={profile.dateOfBirth}
                onChange={change}
                max={new Date().toISOString().slice(0, 10)}
                min="1900-01-01"
              />
              <NewInputField
                id="client-retirement"
                name="retirementAge"
                label="Retirement age (optional)"
                type="number"
                min={1}
                step={1}
                value={profile.retirementAge}
                onChange={change}
                inputProps={{ max: 120 }}
              />
            </div>
            {access?.role === "Administrators" && (
              <CustomSelect label="Linked adviser" required value={adviserEmail} options={access.adviserEmails.map(value => ({ id: value, name: value }))} onChange={event => setAdviserEmail(event.target.value)} clearable={false} />
            )}
            {error && (
              <p className="FP_Content text-error" role="alert">
                {error}
              </p>
            )}
          </form>
        </Popup>
      </main>
    </div>
  );
}
